import { STYLE_SLUG_TO_NAME } from './stateCore.mjs';

/**
 * UI-11 (F4, operator-ruled 2026-08-15) — the style-link row on suburb pages.
 *
 * WHY THIS EXISTS. 435 style pages ("BJJ Gyms in Parramatta") were measured at
 * ZERO inbound anchors — 0 of 43,396 on-site links. They were reachable only
 * from the sitemap, never by clicking. Simulated BFS over the deploy-#22 corpus:
 * linking each suburb page to its own style pages rescues 477 pages from
 * click-unreachability (the 435 styles + 42 gyms whose only inbound anchors came
 * from that unreachable island).
 *
 * ⚠️ WHAT THIS IS *NOT* FOR, stated because a previous framing of mine was wrong
 * and the operator ruled on it: this is NOT a search-indexing fix. All 435 style
 * pages are already in `sitemap-styles-{state}.xml` and Content & SEO measured
 * NSW at 224 of 225 INDEXED (99.6%) while 100% anchor-orphaned. The sitemap does
 * the discovery job. **Do not expect an indexing metric to move, and do not let
 * that expectation be the justification — when it does not move, someone will
 * conclude this build failed.** The defect fixed here is human navigation.
 *
 * ⭐ SOFT-404 SAFETY IS BY CONSTRUCTION, and it is the whole design.
 * Links are derived from the BUILT ROUTE LIST (`scripts/ssg-routes.json`'s
 * `style` bucket), never from gym data. A suburb's gyms may claim a style whose
 * page the generator did not build — deriving from `allStyles` would then emit a
 * link to a URL that serves the noindex not-found shell at HTTP 200, which is
 * exactly the class this programme calls a soft-404 and which no test would
 * catch (the link would look correct and the page would return 200).
 * **If it is not in the route list, it does not get a link.**
 *
 * ⚠️ SCOPE (docs/guard-scope-convention.md):
 * COVERS: turning a list of built style paths into the links for ONE suburb —
 *   the filtering (only this suburb's), the ordering (canonical vocabulary
 *   order, so 199 pages cannot each choose their own), the labels (from the
 *   frozen STYLE_SLUG_TO_NAME map, never typed here), and the heading string.
 * DOES NOT COVER: whether the route list itself is correct or current — that is
 *   the generator's job and this module trusts its input; and it makes no claim
 *   about style pages that exist but are absent from the passed list.
 * ⚠️ NOT YET COVERED BY ANY TEST — the RENDER. lib/suburbStyleLinks.test.ts
 *   asserts this module's derivation only. Nothing yet asserts that
 *   pages/DirectoryPage.tsx renders the row, scopes it to the current route, or
 *   omits it on style pages and the state roots. **That gap is stated here
 *   deliberately rather than left to be assumed closed**: a reader who sees a
 *   tested module beside a rendered feature will otherwise infer the feature is
 *   tested. Written as UI-11's outstanding work, not as an accepted gap.
 * ⚠️ NOT BUILT FROM THE BREADCRUMB PRIMITIVE — and the reason matters, because I
 *   first recorded a FALSE one. It is NOT that the primitive trips the
 *   structured-data gate: that gate counts JSON-LD entries and the primitive emits
 *   none. It is that a style-link row is a set of SIBLINGS, not an ancestry trail,
 *   so breadcrumb markup would announce a hierarchy that does not exist.
 * SIBLING: nothing else derives style links. `lib/blogHubLinks.ts` builds style
 *   URLs for blog CTAs from a different source and for a different purpose.
 */

export interface SuburbStyleLink {
    /** The URL slug, e.g. 'muay-thai'. */
    slug: string;
    /** Canonical display name from the frozen vocabulary, e.g. 'Muay Thai'. */
    label: string;
    /** Root-relative href — the built route, passed through unchanged. */
    href: string;
}

/**
 * The section heading, operator-ruled 2026-08-15 (verbatim: `Styles in {Suburb}`).
 *
 * Content & SEO were asked to rule this copy and DECLINED — their operator ruled
 * copy-authoring outside their reopening's scope — so it came back to the UI/UX
 * operator, who chose this form. It deliberately mirrors the shape of the heading
 * already on the same page ("Gyms in Parramatta") so it reads as a sibling rather
 * than something bolted on, and because the heading names the suburb the links do
 * not repeat it.
 *
 * ⚠️ It must read correctly at BOTH ends of the range — a suburb with one style
 * and a suburb with nine. "Styles in X" does, because it names the category
 * rather than counting it.
 */
export function styleSectionHeading(suburbDisplayName: string): string {
    return `Styles in ${suburbDisplayName}`;
}

/**
 * Canonical vocabulary order, computed once. Ordering the row by this rather than
 * by the route list's own order means all 199 pages present styles in the same
 * sequence regardless of how the generator happened to emit them.
 */
const CANONICAL_ORDER: readonly string[] = Object.freeze(Object.keys(STYLE_SLUG_TO_NAME));

/**
 * The style links for one suburb page.
 *
 * @param builtStylePaths every built style route, e.g. `/nsw/parramatta-2150/gyms/bjj`.
 *        This IS the soft-404 guarantee — see the docblock. Pass the route list,
 *        never a list derived from gym documents.
 * @param suburbPath the suburb page's own path, e.g. `/nsw/parramatta-2150/gyms`.
 * @returns links in canonical vocabulary order; `[]` when the suburb hosts none,
 *        which is the correct result for 789 of the 988 suburb pages.
 */
export function styleLinksForSuburb(
    builtStylePaths: readonly string[],
    suburbPath: string,
): SuburbStyleLink[] {
    // Trailing-slash tolerance on the suburb path only: the route list is
    // generated without them, but callers read the path from a router/location
    // where a trailing slash is possible. Normalising the PREFIX we build rather
    // than every element keeps this O(n) with one allocation.
    const base = suburbPath.endsWith('/') ? suburbPath.slice(0, -1) : suburbPath;
    const prefix = `${base}/`;

    const bySlug = new Map<string, string>();
    for (const path of builtStylePaths) {
        if (!path.startsWith(prefix)) continue;
        const slug = path.slice(prefix.length);
        // Reject anything with a further segment: `/gyms/bjj/x` is not a style
        // page for this suburb, and a gym profile (`…-gABC123`) is not either.
        // Membership in the frozen vocabulary settles both without a second regex.
        if (!slug || slug.includes('/')) continue;
        if (!Object.prototype.hasOwnProperty.call(STYLE_SLUG_TO_NAME, slug)) continue;
        bySlug.set(slug, path);
    }

    const out: SuburbStyleLink[] = [];
    for (const slug of CANONICAL_ORDER) {
        const href = bySlug.get(slug);
        if (!href) continue;
        out.push({ slug, label: STYLE_SLUG_TO_NAME[slug]!, href });
    }
    return out;
}
